import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { getOdcHeaderData, insertNewOdcOrder } from '../utilities/api';
import OdcSalesHeader from './OdcSalesHeader';
import OdcSalesContent from './OdcSalesContent';

const OdcSalesPage = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    const [allData, setAllData] = useState({ companies: [], categories: [], products: [], settings: [] });
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [remarks, setRemarks] = useState('');
    const [orderTakenBy, setOrderTakenBy] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerGst, setCustomerGst] = useState('');
    const [orderDate, setOrderDate] = useState(new Date());
    const [orderTime, setOrderTime] = useState(new Date());
    const [deliveryDate, setDeliveryDate] = useState(new Date());
    const [deliveryTime, setDeliveryTime] = useState(null);
    const [sessionInfo, setSessionInfo] = useState({ ubranch: null, userid: null, branchid: null });
    const [loading, setLoading] = useState(false);

    // Single fetch on mount — all data loaded at once
    useEffect(() => {
        const bid = localStorage.getItem('branchid');
        setSessionInfo({ ubranch: localStorage.getItem('branch'), userid: localStorage.getItem('userid'), branchid: bid });

        const fetchAll = async () => {
            try {
                const result = await getOdcHeaderData({ branch_id: bid });
                setAllData({
                    companies: result.companies || [],
                    categories: result.categories || [],
                    products: result.products || [],
                    settings: result.settings || []
                });
            } catch (err) {
                console.error('Error fetching header data:', err);
            }
        };
        fetchAll();
    }, []);

    // Reset downstream selections when order type changes
    useEffect(() => {
        setSelectedCategory(null);
        setSelectedProduct(null);
        setSelectedPaymentMode(null);
    }, [selectedCompany]);

    // Reset product when category changes
    useEffect(() => {
        setSelectedProduct(null);
    }, [selectedCategory]);

    // Client-side chained filtering
    const filteredCategories = selectedCompany ? allData.categories.filter((c) => c.company_id === selectedCompany.company_id) : [];

    const filteredProducts = selectedCategory ? allData.products.filter((p) => p.prcat_cat_id === selectedCategory.code) : [];

    const addToCart = () => {
        if (!selectedProduct) return;
        const exists = cartItems.find((i) => i.pr_id === selectedProduct.pr_id);
        if (exists) {
            setCartItems(cartItems.map((i) => (i.uniqueId === exists.uniqueId ? { ...i, quantity: i.quantity + 1 } : i)));
        } else {
            setCartItems([
                ...cartItems,
                {
                    uniqueId: `new-${selectedProduct.pr_id}-${Date.now()}`,
                    id: selectedProduct.pr_id,
                    name: selectedProduct.product_name,
                    basic_rate: parseFloat(selectedProduct.basic_rate),
                    tax: parseFloat(selectedProduct.basic_tax_percent),
                    quantity: 1,
                    pr_id: selectedProduct.pr_id,
                    category_id: selectedProduct.prcat_cat_id
                }
            ]);
        }
        setSelectedProduct(null);
    };

    const totalSummary = cartItems.reduce(
        (acc, i) => {
            const sub = (i.quantity || 0) * i.basic_rate;
            return { subtotal: acc.subtotal + sub, tax: acc.tax + (sub * i.tax) / 100 };
        },
        { subtotal: 0, tax: 0 }
    );

    const submitFreshOrder = async () => {
        setLoading(true);
        const payload = {
            branch_id: sessionInfo.branchid,
            user_id: sessionInfo.userid,
            branchnames: sessionInfo.ubranch,
            company_id: selectedCompany ? selectedCompany.company_id : null,
            company_name: selectedCompany ? selectedCompany.name : null,
            is_credit: selectedCompany?.name === 'ODC' ? 1 : 0,
            mop: selectedPaymentMode ? selectedPaymentMode.name : 'CREDIT',
            final_mop: selectedPaymentMode ? selectedPaymentMode.name : 'CREDIT',
            order_taken_by: orderTakenBy,
            customer_name: customerName,
            customer_phone: customerPhone,
            customer_gst: customerGst,
            remarks,
            order_date: moment(orderDate).format('YYYY-MM-DD'),
            order_time: moment(orderTime).format('hh:mm A'),
            delivery_date: moment(deliveryDate).format('YYYY-MM-DD'),
            delivery_time: deliveryTime ? moment(deliveryTime).format('hh:mm A') : '',
            subtotal: totalSummary.subtotal,
            total_tax_amount: totalSummary.tax,
            grand_total: totalSummary.subtotal + totalSummary.tax,
            items: cartItems.map((item) => ({
                product_id: item.pr_id,
                product_name: item.name,
                pr_id: item.id,
                quantity: item.quantity,
                basic_rate: item.basic_rate,
                taxableamount: item.quantity * item.basic_rate,
                tax_percentage: item.tax,
                tax_amount: (item.quantity * item.basic_rate * item.tax) / 100,
                category_id: item.category_id,
                splitted_tax_pecetage: item.tax / 2,
                splitted_gst: (item.quantity * item.basic_rate * item.tax) / 100 / 2
            }))
        };

        try {
            const response = await insertNewOdcOrder(payload);
            if (response.status === true) {
                toast.current.show({ severity: 'success', summary: 'Order Placed', detail: `Invoice ${response.bill_no} generated.`, life: 5000 });
                setTimeout(() => navigate('/odcorderlist'), 2000);
            } else {
                toast.current.show({ severity: 'error', summary: 'Order Failed', detail: response.message || 'Error saving order' });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-3">
            <Toast ref={toast} />
            <ConfirmDialog />
            <OdcSalesHeader sessionInfo={sessionInfo} onBack={() => navigate('/odcorderlist')} />
            <OdcSalesContent
                allData={allData}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                filteredCategories={filteredCategories}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                filteredProducts={filteredProducts}
                cartItems={cartItems}
                setCartItems={setCartItems}
                onAddToCart={addToCart}
                customerName={customerName}
                setCustomerName={setCustomerName}
                customerPhone={customerPhone}
                setCustomerPhone={setCustomerPhone}
                customerGst={customerGst}
                setCustomerGst={setCustomerGst}
                orderDate={orderDate}
                setOrderDate={setOrderDate}
                orderTime={orderTime}
                setOrderTime={setOrderTime}
                deliveryDate={deliveryDate}
                setDeliveryDate={setDeliveryDate}
                deliveryTime={deliveryTime}
                setDeliveryTime={setDeliveryTime}
                orderTakenBy={orderTakenBy}
                setOrderTakenBy={setOrderTakenBy}
                remarks={remarks}
                setRemarks={setRemarks}
                selectedPaymentMode={selectedPaymentMode}
                setSelectedPaymentMode={setSelectedPaymentMode}
                totalSummary={totalSummary}
                loading={loading}
                onSubmit={submitFreshOrder}
            />
        </div>
    );
};

export default OdcSalesPage;
